<template><markdown-wrapper><h1 id="typeahead">Typeahead <a class="header-anchor" href="#typeahead">&#128279;</a></h1>
<blockquote>
<p>A basic, easily extended component for quickly creating elegant typeaheads with any form text input.</p>
</blockquote>
<p><ins class="adsbygoogle"
     style="display:block; text-align:center;"
     data-ad-layout="in-article"
     data-ad-format="fluid"
     data-ad-client="ca-pub-4714899946256166"
     data-ad-slot="4603582855"></ins></p>
<h2 id="example">Example <a class="header-anchor" href="#example">&#128279;</a></h2>
<p>Use <code v-pre>v-model</code> to bind the input value, and <code v-pre>target</code> to point to the ideal input element.</p>
<p>If you want to clear the <code v-pre>v-model</code> of typeahead, simply set it to <code v-pre>null</code>, the corresponding input value will also be cleared.</p>
<typeahead-example ref="typeahead-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;model=states[0]&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Set to Alabama<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;model=null&quot;</span>&gt;</span>Clear<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">hr</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">label</span> <span class="hljs-attr">for</span>=<span class="hljs-string">&quot;input&quot;</span>&gt;</span>States of America:<span class="hljs-tag">&lt;/<span class="hljs-name">label</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">input</span> <span class="hljs-attr">id</span>=<span class="hljs-string">&quot;input&quot;</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;form-control&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;text&quot;</span> <span class="hljs-attr">placeholder</span>=<span class="hljs-string">&quot;Type to search...&quot;</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">typeahead</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;model&quot;</span> <span class="hljs-attr">target</span>=<span class="hljs-string">&quot;#input&quot;</span> <span class="hljs-attr">:data</span>=<span class="hljs-string">&quot;states&quot;</span> <span class="hljs-attr">item-key</span>=<span class="hljs-string">&quot;name&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">br</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">v-show</span>=<span class="hljs-string">&quot;model&quot;</span>&gt;</span>You selected {{model}}<span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">import</span> states <span class="hljs-keyword">from</span> <span class="hljs-string">&#x27;../../assets/data/states.json&#x27;</span>

  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">model</span>: <span class="hljs-string">&#x27;&#x27;</span>,
        <span class="hljs-attr">states</span>: states.<span class="hljs-property">data</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- typeahead-example.vue --&gt;</span>
</code></pre>
<h2 id="target">Target <a class="header-anchor" href="#target">&#128279;</a></h2>
<p>A <code v-pre>target</code> can be:</p>
<ul>
<li>Selector that can be recognized by <code v-pre>querySelect</code>.</li>
<li>Reference to Element.</li>
<li>Reference to Component.</li>
</ul>
<p>Note that if you use a component reference, the corresponding component's root element must be an input element.</p>
<p>An example using element reference target:</p>
<typeahead-target ref="typeahead-target"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">label</span>&gt;</span>States of America:<span class="hljs-tag">&lt;/<span class="hljs-name">label</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">input</span> <span class="hljs-attr">ref</span>=<span class="hljs-string">&quot;input&quot;</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;form-control&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;text&quot;</span> <span class="hljs-attr">placeholder</span>=<span class="hljs-string">&quot;Type to search...&quot;</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">typeahead</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;model&quot;</span> <span class="hljs-attr">:target</span>=<span class="hljs-string">&quot;target&quot;</span> <span class="hljs-attr">:data</span>=<span class="hljs-string">&quot;states&quot;</span> <span class="hljs-attr">item-key</span>=<span class="hljs-string">&quot;name&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">br</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">v-show</span>=<span class="hljs-string">&quot;model&quot;</span>&gt;</span>You selected {{model}}<span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">model</span>: <span class="hljs-string">&#x27;&#x27;</span>,
        <span class="hljs-attr">target</span>: <span class="hljs-literal">null</span>,
        <span class="hljs-attr">states</span>: states.<span class="hljs-property">data</span> <span class="hljs-comment">// import states from &#x27;../../assets/data/states.json&#x27;</span>
      }
    },
    <span class="hljs-title function_">mounted</span> () {
      <span class="hljs-variable language_">this</span>.<span class="hljs-property">target</span> = <span class="hljs-variable language_">this</span>.<span class="hljs-property">$refs</span>.<span class="hljs-property">input</span>
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- typeahead-target.vue --&gt;</span>
</code></pre>
<h2 id="match-start">Match start <a class="header-anchor" href="#match-start">&#128279;</a></h2>
<p>Match from the head of item.</p>
<p><strong>Note</strong>: Only work in local data query mode.</p>
<typeahead-match-start ref="typeahead-match-start"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">label</span> <span class="hljs-attr">for</span>=<span class="hljs-string">&quot;input-2&quot;</span>&gt;</span>States of America:<span class="hljs-tag">&lt;/<span class="hljs-name">label</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">input</span> <span class="hljs-attr">id</span>=<span class="hljs-string">&quot;input-2&quot;</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;form-control&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;text&quot;</span> <span class="hljs-attr">placeholder</span>=<span class="hljs-string">&quot;Type to search...&quot;</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">typeahead</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;model&quot;</span> <span class="hljs-attr">target</span>=<span class="hljs-string">&quot;#input-2&quot;</span> <span class="hljs-attr">match-start</span> <span class="hljs-attr">:data</span>=<span class="hljs-string">&quot;states&quot;</span> <span class="hljs-attr">item-key</span>=<span class="hljs-string">&quot;name&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">br</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">v-show</span>=<span class="hljs-string">&quot;model&quot;</span>&gt;</span>You selected {{model}}<span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">model</span>: <span class="hljs-string">&#x27;&#x27;</span>,
        <span class="hljs-attr">states</span>: states.<span class="hljs-property">data</span> <span class="hljs-comment">// import states from &#x27;../../assets/data/states.json&#x27;</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- typeahead-match-start.vue --&gt;</span>
</code></pre>
<h2 id="force-select">Force select <a class="header-anchor" href="#force-select">&#128279;</a></h2>
<p>Force user to select from the options or the model will be empty.</p>
<typeahead-force-select ref="typeahead-force-select"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">label</span> <span class="hljs-attr">for</span>=<span class="hljs-string">&quot;input-3&quot;</span>&gt;</span>States of America:<span class="hljs-tag">&lt;/<span class="hljs-name">label</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">input</span> <span class="hljs-attr">id</span>=<span class="hljs-string">&quot;input-3&quot;</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;form-control&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;text&quot;</span> <span class="hljs-attr">placeholder</span>=<span class="hljs-string">&quot;Type to search...&quot;</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">typeahead</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;model&quot;</span> <span class="hljs-attr">target</span>=<span class="hljs-string">&quot;#input-3&quot;</span> <span class="hljs-attr">force-select</span> <span class="hljs-attr">:data</span>=<span class="hljs-string">&quot;states&quot;</span> <span class="hljs-attr">item-key</span>=<span class="hljs-string">&quot;name&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">br</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">v-show</span>=<span class="hljs-string">&quot;model&quot;</span>&gt;</span>You selected {{model}}<span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">model</span>: <span class="hljs-string">&#x27;&#x27;</span>,
        <span class="hljs-attr">states</span>: states.<span class="hljs-property">data</span> <span class="hljs-comment">// import states from &#x27;../../assets/data/states.json&#x27;</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- typeahead-force-select.vue --&gt;</span>
</code></pre>
<h2 id="async-query">Async query <a class="header-anchor" href="#async-query">&#128279;</a></h2>
<p>You can simply use <code v-pre>async-src</code> to perform an AJAX query with built-in query function, or <code v-pre>async-function</code> for a custom one if you need more than that.</p>
<p><strong>Note</strong>: <code v-pre>ignore-case</code> and <code v-pre>match-start</code> won't work in async query mode.</p>
<p>An example using <code v-pre>async-src</code>:</p>
<typeahead-async-query ref="typeahead-async-query"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">label</span> <span class="hljs-attr">for</span>=<span class="hljs-string">&quot;input-4&quot;</span>&gt;</span>Users of Github:<span class="hljs-tag">&lt;/<span class="hljs-name">label</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">input</span> <span class="hljs-attr">id</span>=<span class="hljs-string">&quot;input-4&quot;</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;form-control&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;text&quot;</span> <span class="hljs-attr">placeholder</span>=<span class="hljs-string">&quot;Type to search...&quot;</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">typeahead</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;model&quot;</span> <span class="hljs-attr">target</span>=<span class="hljs-string">&quot;#input-4&quot;</span> <span class="hljs-attr">async-src</span>=<span class="hljs-string">&quot;https://api.github.com/search/users?q=&quot;</span> <span class="hljs-attr">async-key</span>=<span class="hljs-string">&quot;items&quot;</span> <span class="hljs-attr">item-key</span>=<span class="hljs-string">&quot;login&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">br</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">v-show</span>=<span class="hljs-string">&quot;model&quot;</span>&gt;</span>You selected {{model}}<span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">model</span>: <span class="hljs-string">&#x27;&#x27;</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- typeahead-async-query.vue --&gt;</span>
</code></pre>
<h2 id="custom-template">Custom template <a class="header-anchor" href="#custom-template">&#128279;</a></h2>
<p>Use the <code v-pre>item</code> scoped slot to override the typeahead item's template.</p>
<ul>
<li>Use <code v-pre>slot-scope=&quot;props&quot;</code> in Vue 2.5+, otherwise <code v-pre>scope=&quot;props&quot;</code>.</li>
<li>The items list will be <code v-pre>props.items</code>.</li>
<li>The current active item index will be <code v-pre>props.activeIndex</code>.</li>
<li>Use <code v-pre>props.select(item)</code> to select item.</li>
<li>(Optional) Use <code v-pre>props.highlight(item)</code> to highlight search keywords in item.</li>
</ul>
<p>An example with custom template and <code v-pre>async-function</code>:</p>
<typeahead-custom-template ref="typeahead-custom-template"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">label</span> <span class="hljs-attr">for</span>=<span class="hljs-string">&quot;input-5&quot;</span>&gt;</span>Users of Github:<span class="hljs-tag">&lt;/<span class="hljs-name">label</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">input</span> <span class="hljs-attr">id</span>=<span class="hljs-string">&quot;input-5&quot;</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;form-control&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;text&quot;</span> <span class="hljs-attr">placeholder</span>=<span class="hljs-string">&quot;Type to search...&quot;</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">typeahead</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;model&quot;</span> <span class="hljs-attr">target</span>=<span class="hljs-string">&quot;#input-5&quot;</span> <span class="hljs-attr">:async-function</span>=<span class="hljs-string">&quot;queryFunction&quot;</span> <span class="hljs-attr">item-key</span>=<span class="hljs-string">&quot;login&quot;</span>&gt;</span>
      <span class="hljs-tag">&lt;<span class="hljs-name">template</span> <span class="hljs-attr">slot</span>=<span class="hljs-string">&quot;item&quot;</span> <span class="hljs-attr">slot-scope</span>=<span class="hljs-string">&quot;props&quot;</span>&gt;</span>
        <span class="hljs-tag">&lt;<span class="hljs-name">li</span> <span class="hljs-attr">v-for</span>=<span class="hljs-string">&quot;(item, index) in props.items&quot;</span> <span class="hljs-attr">:class</span>=<span class="hljs-string">&quot;{active:props.activeIndex===index}&quot;</span>&gt;</span>
          <span class="hljs-tag">&lt;<span class="hljs-name">a</span> <span class="hljs-attr">role</span>=<span class="hljs-string">&quot;button&quot;</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;props.select(item)&quot;</span>&gt;</span>
            <span class="hljs-tag">&lt;<span class="hljs-name">img</span> <span class="hljs-attr">width</span>=<span class="hljs-string">&quot;22px&quot;</span> <span class="hljs-attr">height</span>=<span class="hljs-string">&quot;22px&quot;</span> <span class="hljs-attr">:src</span>=<span class="hljs-string">&quot;item.avatar_url + &#x27;&amp;s=40&#x27;&quot;</span>&gt;</span>
            <span class="hljs-tag">&lt;<span class="hljs-name">span</span> <span class="hljs-attr">v-html</span>=<span class="hljs-string">&quot;props.highlight(item)&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">span</span>&gt;</span>
          <span class="hljs-tag">&lt;/<span class="hljs-name">a</span>&gt;</span>
        <span class="hljs-tag">&lt;/<span class="hljs-name">li</span>&gt;</span>
      <span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
    <span class="hljs-tag">&lt;/<span class="hljs-name">typeahead</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">br</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">v-show</span>=<span class="hljs-string">&quot;model&quot;</span>&gt;</span>You selected {{model}}<span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">import</span> axios <span class="hljs-keyword">from</span> <span class="hljs-string">&#x27;axios&#x27;</span> <span class="hljs-comment">// https://github.com/axios/axios</span>

  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">model</span>: <span class="hljs-string">&#x27;&#x27;</span>
      }
    },
    <span class="hljs-attr">methods</span>: {
      <span class="hljs-title function_">queryFunction</span> (query, done) {
        axios.<span class="hljs-title function_">get</span>(<span class="hljs-string">&#x27;https://api.github.com/search/users?q=&#x27;</span> + query)
          .<span class="hljs-title function_">then</span>(<span class="hljs-function"><span class="hljs-params">res</span> =&gt;</span> {
            <span class="hljs-title function_">done</span>(res.<span class="hljs-property">data</span>.<span class="hljs-property">items</span>)
          })
          .<span class="hljs-title function_">catch</span>(<span class="hljs-function"><span class="hljs-params">err</span> =&gt;</span> {
            <span class="hljs-comment">// any error handler</span>
          })
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- typeahead-custom-template.vue --&gt;</span>
</code></pre>
<h1 id="api-reference">API Reference <a class="header-anchor" href="#api-reference">&#128279;</a></h1>
<h2 id="typeahead-1"><a href="https://github.com/wxsms/uiv/blob/0.x/src/components/typeahead/Typeahead.vue">Typeahead</a> <a class="header-anchor" href="#typeahead-1">&#128279;</a></h2>
<h3 id="props">Props <a class="header-anchor" href="#props">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Type</th>
<th>Default</th>
<th>Required</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>v-model</code></td>
<td></td>
<td></td>
<td>✔</td>
<td>The input or selected value.</td>
</tr>
<tr>
<td><code v-pre>target</code></td>
<td></td>
<td></td>
<td>✔</td>
<td>The input element to bind with. Can be a select or reference to Element / Component.</td>
</tr>
<tr>
<td><code v-pre>data</code></td>
<td>Array</td>
<td></td>
<td></td>
<td>The local auto-complete query data.</td>
</tr>
<tr>
<td><code v-pre>item-key</code></td>
<td>String</td>
<td></td>
<td></td>
<td>Value of each <code v-pre>data[key]</code> to show, leave blank to use the data object.</td>
</tr>
<tr>
<td><code v-pre>append-to-body</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Append the typeahead dropdown to body.</td>
</tr>
<tr>
<td><code v-pre>ignore-case</code></td>
<td>Boolean</td>
<td>true</td>
<td></td>
<td>Ignore input case while matching. Only work in local data mode.</td>
</tr>
<tr>
<td><code v-pre>match-start</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Match from the head of item. Only work in local data mode.</td>
</tr>
<tr>
<td><code v-pre>force-select</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Force user to select from the options or the model will be empty.</td>
</tr>
<tr>
<td><code v-pre>force-clear</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Clear the input if no valid options has selected in force-select mode.</td>
</tr>
<tr>
<td><code v-pre>open-on-focus</code></td>
<td>Boolean</td>
<td>true</td>
<td></td>
<td>Open the typeahead dropdown on input focus.</td>
</tr>
<tr>
<td><code v-pre>open-on-empty</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Open the typeahead dropdown to show suggestions even if input is empty.</td>
</tr>
<tr>
<td><code v-pre>preselect</code></td>
<td>Boolean</td>
<td>true</td>
<td></td>
<td>Select the first item that matches the query automatically.</td>
</tr>
<tr>
<td><code v-pre>limit</code></td>
<td>Number</td>
<td>10</td>
<td></td>
<td>Limit the options size.</td>
</tr>
<tr>
<td><code v-pre>async-src</code></td>
<td>String</td>
<td></td>
<td></td>
<td>The ajax url to fetch data using GET method, query string will be append to the end of this prop value, should return JSON object or array.</td>
</tr>
<tr>
<td><code v-pre>async-key</code></td>
<td>String</td>
<td></td>
<td></td>
<td>The async JSON key to render, leave blank to use the original json object (should be Array).</td>
</tr>
<tr>
<td><code v-pre>async-function</code></td>
<td>Function</td>
<td></td>
<td></td>
<td>The custom async query function with 2 params: <code v-pre>query</code> as the user input, and <code v-pre>done</code> as the callback function with array data (note that <code v-pre>async-key</code> won't work with this). See the example in Custom Template section for details.</td>
</tr>
<tr>
<td><code v-pre>debounce</code></td>
<td>Number</td>
<td>200</td>
<td></td>
<td>Debounce the input for specify milliseconds while in async mode.</td>
</tr>
</tbody>
</table></div><h3 id="slots">Slots <a class="header-anchor" href="#slots">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>item</code></td>
<td>Use this scoped slot to override the typeahead item's template.</td>
</tr>
<tr>
<td><code v-pre>empty</code></td>
<td>(0.25.0+) Slot content will be displayed while no results matched (if the slot exist).</td>
</tr>
</tbody>
</table></div><h3 id="events">Events <a class="header-anchor" href="#events">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>loading</code></td>
<td>Async loading.</td>
</tr>
<tr>
<td><code v-pre>loaded</code></td>
<td>Async load complete.</td>
</tr>
<tr>
<td><code v-pre>loaded-error</code></td>
<td>Async load complete with error.</td>
</tr>
<tr>
<td><code v-pre>input</code></td>
<td>Item selected</td>
</tr>
</tbody>
</table></div></markdown-wrapper></template><script>
  import states from '../../assets/data/states.json'

  

  

  

  

  

  import axios from 'axios' // https://github.com/axios/axios

  
export default {components:{'typeahead-example':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <btn @click=\"model=states[0]\" type=\"primary\">Set to Alabama</btn>\n    <btn @click=\"model=null\">Clear</btn>\n    <hr/>\n    <label for=\"input\">States of America:</label>\n    <input id=\"input\" class=\"form-control\" type=\"text\" placeholder=\"Type to search...\">\n    <typeahead v-model=\"model\" target=\"#input\" :data=\"states\" item-key=\"name\"/>\n    <br/>\n    <alert v-show=\"model\">You selected {{model}}</alert>\n  </section>\n</div>",
    data () {
      return {
        model: '',
        states: states.data
      }
    }
  },'typeahead-target':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <label>States of America:</label>\n    <input ref=\"input\" class=\"form-control\" type=\"text\" placeholder=\"Type to search...\">\n    <typeahead v-model=\"model\" :target=\"target\" :data=\"states\" item-key=\"name\"/>\n    <br/>\n    <alert v-show=\"model\">You selected {{model}}</alert>\n  </section>\n</div>",
    data () {
      return {
        model: '',
        target: null,
        states: states.data // import states from '../../assets/data/states.json'
      }
    },
    mounted () {
      this.target = this.$refs.input
    }
  },'typeahead-match-start':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <label for=\"input-2\">States of America:</label>\n    <input id=\"input-2\" class=\"form-control\" type=\"text\" placeholder=\"Type to search...\">\n    <typeahead v-model=\"model\" target=\"#input-2\" match-start :data=\"states\" item-key=\"name\"/>\n    <br/>\n    <alert v-show=\"model\">You selected {{model}}</alert>\n  </section>\n</div>",
    data () {
      return {
        model: '',
        states: states.data // import states from '../../assets/data/states.json'
      }
    }
  },'typeahead-force-select':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <label for=\"input-3\">States of America:</label>\n    <input id=\"input-3\" class=\"form-control\" type=\"text\" placeholder=\"Type to search...\">\n    <typeahead v-model=\"model\" target=\"#input-3\" force-select :data=\"states\" item-key=\"name\"/>\n    <br/>\n    <alert v-show=\"model\">You selected {{model}}</alert>\n  </section>\n</div>",
    data () {
      return {
        model: '',
        states: states.data // import states from '../../assets/data/states.json'
      }
    }
  },'typeahead-async-query':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <label for=\"input-4\">Users of Github:</label>\n    <input id=\"input-4\" class=\"form-control\" type=\"text\" placeholder=\"Type to search...\">\n    <typeahead v-model=\"model\" target=\"#input-4\" async-src=\"https://api.github.com/search/users?q=\" async-key=\"items\" item-key=\"login\"/>\n    <br/>\n    <alert v-show=\"model\">You selected {{model}}</alert>\n  </section>\n</div>",
    data () {
      return {
        model: ''
      }
    }
  },'typeahead-custom-template':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <label for=\"input-5\">Users of Github:</label>\n    <input id=\"input-5\" class=\"form-control\" type=\"text\" placeholder=\"Type to search...\">\n    <typeahead v-model=\"model\" target=\"#input-5\" :async-function=\"queryFunction\" item-key=\"login\">\n      <template slot=\"item\" slot-scope=\"props\">\n        <li v-for=\"(item, index) in props.items\" :class=\"{active:props.activeIndex===index}\">\n          <a role=\"button\" @click=\"props.select(item)\">\n            <img width=\"22px\" height=\"22px\" :src=\"item.avatar_url + '&s=40'\">\n            <span v-html=\"props.highlight(item)\"></span>\n          </a>\n        </li>\n      </template>\n    </typeahead>\n    <br/>\n    <alert v-show=\"model\">You selected {{model}}</alert>\n  </section>\n</div>",
    data () {
      return {
        model: ''
      }
    },
    methods: {
      queryFunction (query, done) {
        axios.get('https://api.github.com/search/users?q=' + query)
          .then(res => {
            done(res.data.items)
          })
          .catch(err => {
            // any error handler
          })
      }
    }
  }}}</script>